import { useQuery } from '@tanstack/react-query';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

async function fetchAdvisorsForWebsite() {
  // Step 1: Find website document where url == 'https://nomanejes.com'
  const websitesRef = collection(db, 'websites');
  const websiteQuery = query(websitesRef, where('url', '==', 'https://nomanejes.com'));
  const websiteDocs = await getDocs(websiteQuery);

  if (websiteDocs.empty) {
    throw new Error('Website not found in Firestore');
  }

  const websiteId = websiteDocs.docs[0].id;

  // Step 2: Get advisorIds from advisor_websites collection where websiteId matches
  const advisorWebsitesRef = collection(db, 'advisor_websites');
  const advisorWebsiteQuery = query(advisorWebsitesRef, where('websiteId', '==', websiteId));
  const advisorWebsiteDocs = await getDocs(advisorWebsiteQuery);

  const advisorIds = advisorWebsiteDocs.docs.map(doc => doc.data().advisorId);

  if (advisorIds.length === 0) {
    throw new Error('No advisors found for this website');
  }

  // Step 3: Fetch each advisor document
  const advisorPromises = advisorIds.map(advisorId =>
    getDoc(doc(db, 'advisors', advisorId))
  );
  const advisorDocs = await Promise.all(advisorPromises);
  const advisors = advisorDocs.map(doc => ({ id: doc.id, ...doc.data() }));

  // Step 4: Collect unique tag IDs and fetch tag documents
  const tagIds = new Set();
  advisors.forEach(advisor => {
    if (advisor.tags && Array.isArray(advisor.tags)) {
      advisor.tags.forEach(tagId => tagIds.add(tagId));
    }
  });

  const tagPromises = Array.from(tagIds).map(tagId =>
    getDoc(doc(db, 'tags', tagId))
  );
  const tagDocs = await Promise.all(tagPromises);
  const tagsMap = {};
  tagDocs.forEach(doc => {
    if (doc.exists()) {
      tagsMap[doc.id] = doc.data();
    }
  });

  // Step 5: Resolve tag IDs to tag objects
  const advisorsWithResolvedTags = advisors.map(advisor => ({
    ...advisor,
    tags: advisor.tags
      ? advisor.tags.map(tagId => tagsMap[tagId])
      : []
  }));

  return advisorsWithResolvedTags;
}

export function useAdvisors() {
  return useQuery({
    queryKey: ['advisors', 'nomanejes'],
    queryFn: fetchAdvisorsForWebsite,
    staleTime: Infinity,
  });
}
